import { Link } from 'react-router-dom'
import Seo from '../components/Seo'
import Breadcrumbs from '../components/Breadcrumbs'
import { useLocale, localizedHref } from '../i18n/locale'

interface Step { n: number; icon: string; title: string; body: string; tip: string }
interface Tip { title: string; body: string }
interface Faq { q: string; a: string }

interface HowToContent {
  seoTitle: string
  seoDesc: string
  crumbHome: string
  crumbHow: string
  h1: string
  intro: string
  steps: Step[]
  tipLabel: string
  proH2: string
  tips: Tip[]
  faqH2: string
  faqs: Faq[]
  ctaH2: string
  ctaLead: string
  ctaBrowse: string
  ctaPlanner: string
}

const EN: HowToContent = {
  seoTitle: 'How to Use RenoSheets | Step-by-Step Guide',
  seoDesc:
    'Learn how to use RenoSheets calculators to get exact material quantities, build a complete buy list, save your results, and plan your renovation project.',
  crumbHome: 'Home',
  crumbHow: 'How to Use',
  h1: 'How to Use RenoSheets',
  intro:
    "From measurement to complete buy list in under two minutes. Here's exactly how to get the most out of every calculator.",
  steps: [
    {
      n: 1,
      icon: '🧮',
      title: 'Pick a calculator',
      body: 'Browse by category (Paint & Walls, Flooring & Tile, Concrete, Outdoor, Roofing) or jump straight to the tool you need. Each calculator is purpose-built for its material type.',
      tip: 'Not sure which calculator to use? The Calculators page groups them by project type — start there.',
    },
    {
      n: 2,
      icon: '📐',
      title: 'Enter your measurements',
      body: 'Type in your room or area dimensions. All calculators support imperial (feet/inches) and metric (metres). You can add multiple areas — the calculator totals them automatically.',
      tip: 'For irregular rooms, break them into rectangles and add each as a separate area. The totals are summed automatically.',
    },
    {
      n: 3,
      icon: '⚙️',
      title: 'Adjust the assumptions',
      body: 'Every calculator shows the key assumptions (waste %, number of coats, joint width, etc.) and lets you adjust them with a slider or dropdown. The default values follow industry standards.',
      tip: 'Diagonal or patterned tile? Bump waste to 15–20%. Two coats of paint on new drywall? Switch to 2 coats.',
    },
    {
      n: 4,
      icon: '📋',
      title: 'Review your complete materials list',
      body: 'The result panel shows the primary quantity you need plus every consumable that goes with it. Items are split into "need to buy" and "recommended tool" categories.',
      tip: 'Check the affiliate links for quick price comparison — clicking through to a retailer supports the site at no cost to you.',
    },
    {
      n: 5,
      icon: '💾',
      title: 'Save to My Sheets',
      body: 'Hit "Save to My Sheets" to store the result in your browser. Saved sheets persist between visits so you can reference them while shopping or hand them off to a contractor.',
      tip: "Saved sheets are stored only in your browser. If you clear your browser data or use a different device, they won't be there.",
    },
    {
      n: 6,
      icon: '🗂️',
      title: 'Use the Project Planner',
      body: 'For multi-trade renovations, create a project in the Planner. Add a task card for each job (tile, paint, drywall…), attach your saved sheet to it, and move cards through Planning → Quoted → In Progress → Done.',
      tip: 'Long-press a card on mobile (or click-drag on desktop) to reorder tasks within a column.',
    },
  ],
  tipLabel: 'Tip:',
  proH2: 'Pro tips for accurate results',
  tips: [
    {
      title: 'Measure twice, calculate once',
      body: 'Take your measurements before opening a calculator. For paint, measure wall height and room perimeter; for tile, the floor or wall area.',
    },
    {
      title: 'Same dye lot matters',
      body: 'Always buy all your tile (or paint) in one purchase from the same production lot. Colours vary between batches — the calculator tells you how much you need, so buy it all at once.',
    },
    {
      title: 'Round up at the register, not here',
      body: 'Our calculators show the mathematical quantity. We recommend rounding up to the nearest whole sellable unit (bag, gallon, box) at the store, not pre-padding the estimate.',
    },
    {
      title: 'Save a sheet before you leave',
      body: 'Save your results to My Sheets before heading to the store. The saved sheet shows all quantities and a quick buy list you can reference on your phone.',
    },
  ],
  faqH2: 'Frequently asked questions',
  faqs: [
    {
      q: 'Is my data saved if I close the browser?',
      a: 'Yes — any sheet you explicitly save with the "Save to My Sheets" button is stored in your browser\'s IndexedDB and persists between visits. Project planner data is also saved automatically as you work.',
    },
    {
      q: 'Can I use RenoSheets on my phone at the jobsite?',
      a: "Yes. RenoSheets is mobile-first and works in any modern mobile browser. Once you've loaded the page, it works even with a poor signal since everything runs in the browser.",
    },
    {
      q: 'Can I export or print my materials list?',
      a: "Use your browser's built-in print function (Ctrl+P / Cmd+P) on any calculator page — the materials list prints cleanly. A dedicated PDF export button is on our roadmap.",
    },
    {
      q: 'How accurate are the estimates?',
      a: "Calculations use standard industry formulas and coverage rates. They're as accurate as the measurements you enter. All estimates are for planning purposes — always verify before purchasing, and consult a professional for structural or safety-critical work.",
    },
  ],
  ctaH2: 'Ready to get started?',
  ctaLead: 'Pick a calculator and have your materials list in two minutes.',
  ctaBrowse: 'Browse Calculators',
  ctaPlanner: 'Open the Planner',
}

const FR: HowToContent = {
  seoTitle: 'Comment utiliser RenoSheets | Guide étape par étape',
  seoDesc:
    'Apprenez à utiliser les calculateurs RenoSheets pour obtenir les quantités exactes de matériaux, bâtir une liste d’achats complète, enregistrer vos résultats et planifier votre projet de rénovation.',
  crumbHome: 'Accueil',
  crumbHow: 'Comment l’utiliser',
  h1: 'Comment utiliser RenoSheets',
  intro:
    'De la mesure à la liste d’achats complète en moins de deux minutes. Voici exactement comment tirer le maximum de chaque calculateur.',
  steps: [
    {
      n: 1,
      icon: '🧮',
      title: 'Choisissez un calculateur',
      body: 'Parcourez par catégorie (Peinture et murs, Planchers et carrelage, Béton, Extérieur, Toiture) ou allez directement à l’outil qu’il vous faut. Chaque calculateur est conçu sur mesure pour son type de matériau.',
      tip: 'Vous ne savez pas quel calculateur utiliser? La page des calculateurs les regroupe par type de projet — commencez par là.',
    },
    {
      n: 2,
      icon: '📐',
      title: 'Entrez vos mesures',
      body: 'Saisissez les dimensions de votre pièce ou de votre surface. Tous les calculateurs acceptent les unités impériales (pieds/pouces) et métriques (mètres). Vous pouvez ajouter plusieurs surfaces — le calculateur en fait le total automatiquement.',
      tip: 'Pour les pièces irrégulières, divisez-les en rectangles et ajoutez chacun comme une surface distincte. Les totaux sont additionnés automatiquement.',
    },
    {
      n: 3,
      icon: '⚙️',
      title: 'Ajustez les hypothèses',
      body: 'Chaque calculateur affiche les hypothèses clés (% de perte, nombre de couches, largeur des joints, etc.) et vous laisse les ajuster avec un curseur ou un menu déroulant. Les valeurs par défaut suivent les normes de l’industrie.',
      tip: 'Tuiles en diagonale ou à motif? Montez la perte à 15–20 %. Deux couches de peinture sur du gypse neuf? Passez à 2 couches.',
    },
    {
      n: 4,
      icon: '📋',
      title: 'Vérifiez votre liste complète de matériaux',
      body: 'Le panneau de résultats affiche la quantité principale dont vous avez besoin plus chaque consommable qui l’accompagne. Les articles sont répartis entre « à acheter » et « outil recommandé ».',
      tip: 'Consultez les liens d’affiliation pour comparer rapidement les prix — passer par un détaillant soutient le site sans frais pour vous.',
    },
    {
      n: 5,
      icon: '💾',
      title: 'Enregistrez dans Mes feuilles',
      body: 'Cliquez sur « Enregistrer dans Mes feuilles » pour conserver le résultat dans votre navigateur. Les feuilles enregistrées persistent entre les visites, pour les consulter en magasinant ou les remettre à un entrepreneur.',
      tip: 'Les feuilles enregistrées sont stockées uniquement dans votre navigateur. Si vous effacez les données de votre navigateur ou changez d’appareil, elles n’y seront plus.',
    },
    {
      n: 6,
      icon: '🗂️',
      title: 'Utilisez le planificateur de projet',
      body: 'Pour les rénovations à plusieurs métiers, créez un projet dans le planificateur. Ajoutez une carte de tâche pour chaque travail (carrelage, peinture, gypse…), attachez-y votre feuille enregistrée, et déplacez les cartes de Planification → Soumission → En cours → Terminé.',
      tip: 'Maintenez une carte enfoncée sur mobile (ou glissez-la sur ordinateur) pour réorganiser les tâches dans une colonne.',
    },
  ],
  tipLabel: 'Conseil :',
  proH2: 'Conseils de pro pour des résultats précis',
  tips: [
    {
      title: 'Mesurez deux fois, calculez une fois',
      body: 'Prenez vos mesures avant d’ouvrir un calculateur. Pour la peinture, mesurez la hauteur des murs et le périmètre de la pièce; pour le carrelage, la surface du plancher ou du mur.',
    },
    {
      title: 'Le même lot de teinte compte',
      body: 'Achetez toujours tout votre carrelage (ou votre peinture) en un seul achat, du même lot de production. Les couleurs varient d’un lot à l’autre — le calculateur vous dit combien il vous en faut, alors achetez tout d’un coup.',
    },
    {
      title: 'Arrondissez à la caisse, pas ici',
      body: 'Nos calculateurs affichent la quantité mathématique. Nous recommandons d’arrondir à l’unité vendable entière la plus proche (sac, gallon, boîte) en magasin, plutôt que de gonfler l’estimation d’avance.',
    },
    {
      title: 'Enregistrez une feuille avant de partir',
      body: 'Enregistrez vos résultats dans Mes feuilles avant d’aller au magasin. La feuille enregistrée affiche toutes les quantités et une liste d’achats rapide à consulter sur votre téléphone.',
    },
  ],
  faqH2: 'Foire aux questions',
  faqs: [
    {
      q: 'Mes données sont-elles enregistrées si je ferme le navigateur?',
      a: 'Oui — toute feuille que vous enregistrez explicitement avec le bouton « Enregistrer dans Mes feuilles » est stockée dans l’IndexedDB de votre navigateur et persiste entre les visites. Les données du planificateur de projet sont aussi enregistrées automatiquement au fil de votre travail.',
    },
    {
      q: 'Puis-je utiliser RenoSheets sur mon téléphone au chantier?',
      a: 'Oui. RenoSheets est conçu d’abord pour le mobile et fonctionne dans tout navigateur mobile moderne. Une fois la page chargée, ça fonctionne même avec un mauvais signal, puisque tout s’exécute dans le navigateur.',
    },
    {
      q: 'Puis-je exporter ou imprimer ma liste de matériaux?',
      a: 'Utilisez la fonction d’impression intégrée de votre navigateur (Ctrl+P / Cmd+P) sur n’importe quelle page de calculateur — la liste de matériaux s’imprime proprement. Un bouton d’exportation PDF dédié est prévu dans nos plans.',
    },
    {
      q: 'Quelle est la précision des estimations?',
      a: 'Les calculs utilisent des formules et des taux de couverture standards de l’industrie. Ils sont aussi précis que les mesures que vous entrez. Toutes les estimations servent à la planification — vérifiez toujours avant d’acheter, et consultez un professionnel pour les travaux de structure ou critiques pour la sécurité.',
    },
  ],
  ctaH2: 'Prêt à commencer?',
  ctaLead: 'Choisissez un calculateur et obtenez votre liste de matériaux en deux minutes.',
  ctaBrowse: 'Voir les calculateurs',
  ctaPlanner: 'Ouvrir le planificateur',
}

export default function HowToUse() {
  const locale = useLocale()
  const c = locale === 'fr' ? FR : EN
  const L = (en: string) => localizedHref(en, locale)

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      <Seo
        title={c.seoTitle}
        description={c.seoDesc}
        path="/how-to-use/"
      />
      <Breadcrumbs items={[{ name: c.crumbHome, href: L('/') }, { name: c.crumbHow, href: L('/how-to-use/') }]} />

      <h1 className="text-3xl">{c.h1}</h1>
      <p className="mt-2 text-text-muted max-w-2xl">{c.intro}</p>

      {/* Walkthrough video — full container width, native 16:9 aspect ratio
          stays locked via Tailwind's aspect-video so there's never any layout
          shift while the iframe loads. Loaded directly (not lite-embed) per
          the simpler implementation the user requested; if Core Web Vitals
          ever flag the page, swap to a click-to-load thumbnail. */}
      <div className="mt-8 aspect-video w-full overflow-hidden rounded-xl border border-border bg-black shadow-md">
        {/* vq=hd1080 is YouTube's legacy "prefer HD" hint — officially removed
            but still respected by the player on most modern browsers when the
            user's bandwidth + container size allow it. The full-width container
            at desktop sizes already biases the player toward HD; this just
            nudges it harder. Users can still drop quality manually from the
            gear menu if they're on a metered connection. */}
        <iframe
          src="https://www.youtube.com/embed/U-vcxBNZ2eI?si=zDWFhJJfgC0JZubE&vq=hd1080&hd=1"
          title="How to use RenoSheets"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
          className="h-full w-full border-0"
        />
      </div>

      {/* Steps */}
      <ol className="mt-10 space-y-8">
        {c.steps.map((s) => (
          <li key={s.n} className="flex gap-5">
            <div className="flex-shrink-0">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand text-base font-bold text-white">
                {s.n}
              </div>
            </div>
            <div className="rounded-xl border border-border bg-surface p-5 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-xl">{s.icon}</span>
                <h2 className="text-lg font-semibold text-text">{s.title}</h2>
              </div>
              <p className="mt-2 text-text-muted">{s.body}</p>
              <div className="mt-3 rounded-lg bg-brand-tint px-4 py-2.5 text-sm text-brand-text">
                <strong>{c.tipLabel}</strong> {s.tip}
              </div>
            </div>
          </li>
        ))}
      </ol>

      {/* Pro tips */}
      <section className="mt-14">
        <h2 className="text-2xl text-text">{c.proH2}</h2>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {c.tips.map((tip) => (
            <div key={tip.title} className="rounded-xl border border-border bg-surface p-5">
              <h3 className="font-semibold text-text">{tip.title}</h3>
              <p className="mt-1.5 text-sm text-text-muted">{tip.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="mt-14">
        <h2 className="text-2xl text-text">{c.faqH2}</h2>
        <dl className="mt-5 space-y-5">
          {c.faqs.map((f) => (
            <div key={f.q} className="rounded-xl border border-border bg-surface p-5">
              <dt className="font-semibold text-text">{f.q}</dt>
              <dd className="mt-1 text-text-muted">{f.a}</dd>
            </div>
          ))}
        </dl>
      </section>

      {/* CTA */}
      <div className="mt-12 rounded-2xl border border-border bg-brand-tint/40 p-8 text-center">
        <h2 className="text-xl font-semibold text-text">{c.ctaH2}</h2>
        <p className="mt-2 text-text-muted">{c.ctaLead}</p>
        <div className="mt-5 flex flex-wrap justify-center gap-3">
          <Link to={L('/calculators/')} className="rounded-lg bg-primary px-6 py-3 font-semibold text-on-primary hover:opacity-90">
            {c.ctaBrowse}
          </Link>
          <Link to={L('/my-sheets/')} className="rounded-lg border-2 border-brand px-6 py-3 font-semibold text-brand-text hover:bg-brand-tint">
            {c.ctaPlanner}
          </Link>
        </div>
      </div>
    </div>
  )
}
